import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { usePreferencesRealtimeUpdates } from './useRealtimeUpdates';
import { usePreferencesOfflineQueue } from './useOfflineQueue';
import * as Sentry from "@sentry/react";

export type NotificationFrequency = 'realtime' | 'hourly' | 'daily' | 'weekly';

export interface UserPreferences {
  // Core fields
  theme: 'light' | 'dark';
  
  // UI/Display Preferences
  compact_mode?: boolean;
  reduced_motion?: boolean;
  dark_mode?: boolean;
  theme_color?: string;
  
  // Notification Preferences - Email
  email_notifications: boolean;
  email_assessment_reminders?: boolean;
  email_progress_updates?: boolean;
  email_team_activity?: boolean;
  
  // Notification Preferences - Real-time
  push_notifications: boolean;
  browser_notifications?: boolean;
  mobile_push_notifications?: boolean;
  notification_frequency?: NotificationFrequency;
  
  // Feature Flags
  beta_access?: boolean;
  
  // Analytics & Performance
  analytics_enabled?: boolean;
  performance_tracking_enabled?: boolean;
  
  // Communication Preferences
  marketing_emails?: boolean;
  product_updates?: boolean;
  
  // Data & Privacy
  data_collection_consent?: boolean;
  
  // JSONB field for misc preferences
  preferences: Record<string, any>;
}

export function useUserPreferences() {
  const { user, loading } = useAuth(); // Add loading state check
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Setup realtime updates for user preferences
  usePreferencesRealtimeUpdates(user?.id);
  
  // Setup offline queue for failed operations
  const { queuePreferencesSave } = usePreferencesOfflineQueue();

  // Query to fetch user preferences
  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['user-preferences', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('user_preferences')
        .select('id, user_id, theme, compact_mode, reduced_motion, dark_mode, theme_color, email_enabled, email_assessment_reminders, email_progress_updates, email_team_activity, push_notifications, browser_notifications, mobile_push_notifications, notification_frequency, beta_access, analytics_enabled, performance_tracking_enabled, marketing_emails, product_updates, data_collection_consent, preferences, created_at, updated_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      return data;
    },
    enabled: !!user?.id && !loading, // Don't run query during auth loading
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation to update preferences (using direct table update for new columns)
  const updatePreferencesMutation = useMutation({
    mutationFn: async (updates: Partial<UserPreferences>) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Validate notification_frequency if present
      if (updates.notification_frequency && 
          !['realtime', 'hourly', 'daily', 'weekly'].includes(updates.notification_frequency)) {
        throw new Error(`Invalid notification_frequency: ${updates.notification_frequency}`);
      }

      // Create the save operation using direct table update for all new columns
      const saveOperation = async () => {
        const { error } = await supabase
          .from('user_preferences')
          .upsert({
            user_id: user.id,
            ...updates,
          } as any, {
            onConflict: 'user_id'
          });

        if (error) throw error;
      };

      try {
        // Try to save directly first
        await saveOperation();
      } catch (error) {
        // Queue for offline sync if the save failed
        queuePreferencesSave(user.id, updates);
        throw error; // Re-throw to trigger onError
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences', user?.id] });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: { context: 'preference_error', operation: 'user_preferences', action: 'update' },
        extra: { message: 'Failed to update preferences' }
      });
      toast({
        title: "Queued for sync",
        description: "Preferences will be saved when connection is restored.",
        variant: "default",
      });
    },
  });

  // Get current preferences with defaults (cast to any to handle new columns before schema update)
  const prefsData = preferences as any;
  const currentPreferences: UserPreferences = preferences ? {
    theme: (prefsData.theme as 'light' | 'dark') || 'light',
    email_notifications: prefsData.email_notifications ?? true,
    push_notifications: prefsData.push_notifications ?? false,
    preferences: (prefsData.preferences as Record<string, any>) || {},
    
    // UI/Display
    reduced_motion: prefsData.reduced_motion ?? false,
    compact_mode: prefsData.compact_mode ?? false,
    dark_mode: prefsData.dark_mode ?? false,
    theme_color: prefsData.theme_color || 'default',
    
    // Email Notifications
    email_assessment_reminders: prefsData.email_assessment_reminders ?? true,
    email_progress_updates: prefsData.email_progress_updates ?? true,
    email_team_activity: prefsData.email_team_activity ?? true,
    
    // Real-time Notifications
    browser_notifications: prefsData.browser_notifications ?? true,
    mobile_push_notifications: prefsData.mobile_push_notifications ?? true,
    notification_frequency: (prefsData.notification_frequency as NotificationFrequency) || 'realtime',
    
    // Feature Flags
    beta_access: prefsData.beta_access ?? false,
    
    // Analytics & Performance
    analytics_enabled: prefsData.analytics_enabled ?? true,
    performance_tracking_enabled: prefsData.performance_tracking_enabled ?? false,
    
    // Communication
    marketing_emails: prefsData.marketing_emails ?? false,
    product_updates: prefsData.product_updates ?? true,
    
    // Privacy
    data_collection_consent: prefsData.data_collection_consent ?? false,
  } : {
    theme: 'light',
    email_notifications: true,
    push_notifications: false,
    preferences: {},
    reduced_motion: false,
    compact_mode: false,
    dark_mode: false,
    theme_color: 'default',
    email_assessment_reminders: true,
    email_progress_updates: true,
    email_team_activity: true,
    browser_notifications: true,
    mobile_push_notifications: true,
    notification_frequency: 'realtime',
    beta_access: false,
    analytics_enabled: true,
    performance_tracking_enabled: false,
    marketing_emails: false,
    product_updates: true,
    data_collection_consent: false,
  };

  // Update theme
  const updateTheme = useCallback((theme: 'light' | 'dark') => {
    updatePreferencesMutation.mutate({ theme });
  }, [updatePreferencesMutation]);

  // Update notification preferences
  const updateNotifications = useCallback((email_notifications?: boolean, push_notifications?: boolean) => {
    const updates: Partial<UserPreferences> = {};
    if (email_notifications !== undefined) updates.email_notifications = email_notifications;
    if (push_notifications !== undefined) updates.push_notifications = push_notifications;
    updatePreferencesMutation.mutate(updates);
  }, [updatePreferencesMutation]);

  // Update misc preferences (using direct columns for compact_mode and reduced_motion)
  const updateMiscPreferences = useCallback(async (updates: {
    compactMode?: boolean;
    reducedMotion?: boolean;
    [key: string]: any;
  }) => {
    if (!user?.id) throw new Error('User not authenticated');
    
    const directUpdates: any = {};
    
    // Map to correct column names
    if (updates.compactMode !== undefined) {
      directUpdates.compact_mode = updates.compactMode;
    }
    
    if (updates.reducedMotion !== undefined) {
      directUpdates.reduced_motion = updates.reducedMotion;
    }
    
    // Update direct columns if any
    if (Object.keys(directUpdates).length > 0) {
      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          ...directUpdates
        }, {
          onConflict: 'user_id'
        });
      
      if (error) throw error;
    }
    
    // Handle other misc preferences in the JSONB field
    const otherPrefs = { ...updates };
    delete otherPrefs.compactMode;
    delete otherPrefs.reducedMotion;
    
    if (Object.keys(otherPrefs).length > 0) {
      updatePreferencesMutation.mutate({ preferences: otherPrefs });
    }
    
    // Invalidate queries to refresh the data
    queryClient.invalidateQueries({ queryKey: ['user-preferences', user.id] });
  }, [updatePreferencesMutation, user?.id, queryClient]);

  // Generic update function
  const updatePreferences = useCallback((updates: Partial<UserPreferences>) => {
    updatePreferencesMutation.mutate(updates);
  }, [updatePreferencesMutation]);

  // Update single preference field
  const updatePreference = useCallback(<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => {
    updatePreferencesMutation.mutate({ [key]: value } as Partial<UserPreferences>);
  }, [updatePreferencesMutation]);

  // Toggle boolean preference
  const togglePreference = useCallback((key: keyof UserPreferences) => {
    const currentValue = currentPreferences[key];
    if (typeof currentValue === 'boolean') {
      updatePreferencesMutation.mutate({ [key]: !currentValue } as Partial<UserPreferences>);
    }
  }, [currentPreferences, updatePreferencesMutation]);

  // Update notification frequency
  const updateNotificationFrequency = useCallback((frequency: NotificationFrequency) => {
    updatePreferencesMutation.mutate({ notification_frequency: frequency });
  }, [updatePreferencesMutation]);

  // Bulk update email notifications
  const updateEmailNotifications = useCallback((updates: {
    email_notifications?: boolean;
    email_assessment_reminders?: boolean;
    email_progress_updates?: boolean;
    email_team_activity?: boolean;
  }) => {
    updatePreferencesMutation.mutate(updates);
  }, [updatePreferencesMutation]);

  return {
    preferences: currentPreferences,
    isLoading,
    error,
    updateTheme,
    updateNotifications,
    updateMiscPreferences,
    updatePreferences,
    updatePreference,
    togglePreference,
    updateNotificationFrequency,
    updateEmailNotifications,
    isSaving: updatePreferencesMutation.isPending,
  };
}